import * as React from "react";
import { useState } from "react";
import { Box, Typography } from "@mui/material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useNavigate } from "react-router-dom";
import { useGetFieldAdminSitesQuery } from "./fieldAdminApi";
import { CARD_BG, BORDER, AMBER } from "../../Global/colors";
import { FAPageState } from "./FAStates";
import { MONO_FONT_FAMILY, OK_GREEN, OK_GREEN_TEXT, WARN_YELLOW, BAD_RED, BAD_RED_TEXT, teamLabel, relativeTime } from "./designTokens";
import { FAFilterChips, FAPageShell, FAEmptyState } from "./FALayout";

export const STATUS_META = {
  approved: { label: "Approved", bg: "rgba(34,197,94,0.14)", color: OK_GREEN_TEXT },
  pending: { label: "Pending", bg: "rgba(251,191,36,0.14)", color: WARN_YELLOW },
  declined: { label: "Declined", bg: "rgba(239,68,68,0.14)", color: BAD_RED_TEXT },
};

const FILTERS = ["All", "Approved", "Pending", "Declined"];

function dominantZone(sites) {
  const counts = {};
  sites.forEach((site) => { if (site.zone) counts[site.zone] = (counts[site.zone] || 0) + 1; });
  const entries = Object.entries(counts);
  if (!entries.length) return null;
  entries.sort((a, b) => b[1] - a[1]);
  return entries[0][0];
}

export default function Sites() {
  const [activeFilter, setActiveFilter] = useState("All");

  const { data, isSuccess, isError, error, refetch } = useGetFieldAdminSitesQuery();

  if (!isSuccess) return <FAPageState isError={isError} error={error} onRetry={refetch} count={5} tiles={3} />;

  const sites_data = data.sites;

  const approved_count = sites_data.filter((site) => site.status === "approved").length;
  const pending_count = sites_data.filter((site) => site.status === "pending").length;
  const declined_count = sites_data.filter((site) => site.status === "declined").length;
  const forecast_total = sites_data.reduce((sum, site) => sum + site.forecast_installs, 0);
  const actual_total = sites_data.reduce((sum, site) => sum + site.actual_installs, 0);
  const forecast_pct = forecast_total === 0 ? 0 : Math.min(100, Math.round((actual_total / forecast_total) * 100));
  const zone = dominantZone(sites_data);

  const filtered_sites = activeFilter === "All"
    ? sites_data
    : sites_data.filter((site) => site.status === activeFilter.toLowerCase());

  const CHIP_COUNTS = { All: sites_data.length, Approved: approved_count, Pending: pending_count, Declined: declined_count };

  return (
      <FAPageShell>
        {/* Page heading */}
        <Box sx={{ mb: "22px" }}>
          <Typography sx={{ fontSize: { xs: "1.4rem", md: "1.75rem" }, fontWeight: 700, color: "#fff", letterSpacing: "-0.025em", lineHeight: 1.15 }}>
            Sites
          </Typography>
          <Typography sx={{ fontSize: "0.86rem", color: "rgba(255,255,255,0.55)", mt: "3px" }}>
            Pipeline for <Box component="span" sx={{ color: AMBER, fontWeight: 600 }}>{teamLabel(data.team_name, data.team_id)}</Box>
            {zone ? ` · ${zone}` : ""}
          </Typography>
        </Box>

        {/* Summary tiles */}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" }, gap: "12px", mb: "22px" }}>
          <SummaryTile label="Approved" value={approved_count} sub="Active installs" borderColor={OK_GREEN} valueColor={OK_GREEN_TEXT} />
          <SummaryTile label="Pending" value={pending_count} sub="Awaiting approval" borderColor={WARN_YELLOW} valueColor={WARN_YELLOW} />
          <SummaryTile label="Declined" value={declined_count} sub="With reason logged" borderColor={BAD_RED} valueColor={BAD_RED_TEXT} />
          <Box sx={{ backgroundColor: CARD_BG, border: `1px solid ${BORDER}`, borderTop: `2px solid ${AMBER}`, borderRadius: "12px", px: "16px", py: "14px" }}>
            <Typography sx={{ fontSize: "0.62rem", fontWeight: 700, color: "rgba(255,255,255,0.4)", textTransform: "uppercase", letterSpacing: "0.08em", mb: "6px" }}>
              Forecast vs actual
            </Typography>
            <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, color: "#fff", letterSpacing: "-0.02em", lineHeight: 1.05 }}>
              {actual_total}<Box component="span" sx={{ color: "rgba(255,255,255,0.3)", fontWeight: 500 }}> / {forecast_total}</Box>
            </Typography>
            {/* Typed onto each site by Neura ops, not counted from COCs (NEU-779). */}
            <Typography sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.45)", mt: "4px" }}>Entered by Neura ops</Typography>
            <Box sx={{ mt: "8px", height: "4px", backgroundColor: "rgba(255,255,255,0.06)", borderRadius: "2px", overflow: "hidden" }}>
              <Box sx={{ height: "100%", width: `${forecast_pct}%`, background: "linear-gradient(90deg,#FDB940,#E0A020)", borderRadius: "2px" }} />
            </Box>
          </Box>
        </Box>

        {/* Filter chips */}
        <FAFilterChips filters={FILTERS} counts={CHIP_COUNTS} active={activeFilter} onChange={setActiveFilter} />

        {/* Section label */}
        <Box sx={{ pt: "6px", pb: "10px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography sx={{ fontSize: "0.66rem", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700, color: "rgba(255,255,255,0.4)" }}>
            Pipeline · {filtered_sites.length} site{filtered_sites.length === 1 ? "" : "s"}
          </Typography>
          <Typography sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.35)", fontWeight: 500 }}>
            Sorted: most recent update
          </Typography>
        </Box>

        {/* Pipeline list */}
        <Box sx={{ backgroundColor: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: "12px", overflow: "hidden" }}>
          {filtered_sites.length === 0 ? (
            <FAEmptyState py="80px">{activeFilter === "All" ? "No sites in the pipeline yet." : `No ${activeFilter.toLowerCase()} sites.`}</FAEmptyState>
          ) : (
            filtered_sites.map((site, index) => (
              <SiteRow key={site.id} site={site} isLast={index === filtered_sites.length - 1} />
            ))
          )}
        </Box>
      </FAPageShell>
  );
}

function SummaryTile({ label, value, sub, borderColor, valueColor }) {
  return (
    <Box sx={{ backgroundColor: CARD_BG, border: `1px solid ${BORDER}`, borderTop: `2px solid ${borderColor}`, borderRadius: "12px", px: "16px", py: "14px" }}>
      <Typography sx={{ fontSize: "0.62rem", fontWeight: 700, color: "rgba(255,255,255,0.4)", textTransform: "uppercase", letterSpacing: "0.08em", mb: "6px" }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, color: valueColor, letterSpacing: "-0.02em", lineHeight: 1.05 }}>
        {value}
      </Typography>
      <Typography sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.5)", mt: "3px" }}>{sub}</Typography>
    </Box>
  );
}

function SiteRow({ site, isLast }) {
  const navigate = useNavigate();
  const status_meta = STATUS_META[site.status] || { label: site.status, bg: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.5)" };

  return (
    <Box
      onClick={() => navigate(`/field-admin/sites/${encodeURIComponent(site.code)}/`)}
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr auto", sm: "minmax(0,1.8fr) 110px 90px 16px" },
        gap: { xs: "8px", sm: "16px" },
        alignItems: "center",
        px: { xs: "14px", sm: "18px" },
        py: { xs: "12px", sm: "14px" },
        transition: "background 0.15s",
        borderBottom: isLast ? "none" : "1px solid rgba(255,255,255,0.04)",
        cursor: "pointer",
        "&:hover": { backgroundColor: "rgba(253,185,64,0.04)" },
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontFamily: MONO_FONT_FAMILY, fontSize: "0.78rem", fontWeight: 700, color: AMBER, letterSpacing: "0.04em" }}>
          {site.code}
        </Typography>
        <Typography sx={{ color: "rgba(255,255,255,0.9)", fontSize: "0.86rem", fontWeight: 500, mt: "3px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {site.address}
        </Typography>
        <Typography sx={{ color: "rgba(255,255,255,0.42)", fontSize: "0.7rem", mt: "2px" }}>
          {relativeTime(site.updated_at)}
        </Typography>
      </Box>

      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "5px",
          fontSize: "0.68rem",
          fontWeight: 700,
          px: "9px",
          py: "4px",
          borderRadius: "4px",
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          justifySelf: { xs: "end", sm: "start" },
          backgroundColor: status_meta.bg,
          color: status_meta.color,
        }}
      >
        {status_meta.label}
      </Box>

      <Box
        component="span"
        sx={{
          display: { xs: "none", sm: "inline-flex" },
          justifySelf: "start",
          alignItems: "center",
          fontSize: "0.82rem",
          color: "rgba(255,255,255,0.85)",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        <Box component="span" sx={{ fontWeight: 700, color: "#fff" }}>{site.actual_installs}</Box>
        <Box component="span" sx={{ color: "rgba(255,255,255,0.3)", mx: "2px" }}>/</Box>
        <Box component="span" sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.74rem" }}>{site.forecast_installs}</Box>
      </Box>

      <ArrowForwardIosIcon sx={{ display: { xs: "none", sm: "block" }, color: "rgba(255,255,255,0.3)", fontSize: "0.85rem", justifySelf: "end" }} />
    </Box>
  );
}
